import { useCallback } from 'react';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useFormState } from 'modules/taboola-common-frontend-modules/forms';
import { useMultiLayerPathParams } from 'modules/taboola-common-frontend-modules/multi-layer-drawer/hooks/useMultiLayerPathParams';
import { useLandingPagesApi } from 'services/api/landingPagesApi';
import { LANDING_PAGES_ROUTE } from '../config';
import { INITIAL_CHAT_STATE } from '../consts/chatState';
import { INITIAL_VERSION, INITIAL_TOTAL_VERSIONS } from '../consts/versionConstants';
import { useHandleChatSubmit } from './useHandleChatSubmit';
import { useHandlePublish } from './useHandlePublish';

export const useLandingPageEditorState = () => {
    const { getLandingPage, getVersions } = useLandingPagesApi();
    const chatSubmitHandler = useHandleChatSubmit();
    const publishHandler = useHandlePublish();
    const { landingPageId } = useMultiLayerPathParams();
    const { accountName } = useAccount();

    // Create a callback for fetching landing page data and version metadata in parallel
    const formDataFetcher = useCallback(async () => {
        // Load both landing page data and version metadata in parallel
        const [landingPage, versionsResponse] = await Promise.all([
            getLandingPage({ accountName, landingPageId }),
            getVersions({ accountName, landingPageId }).catch(error => {
                console.error('Failed to fetch versions for landing page:', landingPageId, error);
                return { totalVersions: INITIAL_TOTAL_VERSIONS }; // Fallback but log the error
            }),
        ]);

        return {
            ...landingPage,
            landingPageId,
            chatHistory: INITIAL_CHAT_STATE,
            // versionNumber comes from the landing page itself (from backend response)
            // For older landing pages without versionNumber, default to 0
            // Ensure it's a proper number, not a string
            versionNumber: Number(landingPage.versionNumber) || INITIAL_VERSION,
            // totalVersions comes from the versions endpoint
            totalVersions: versionsResponse?.totalVersions || INITIAL_TOTAL_VERSIONS,
        };
    }, [accountName, landingPageId, getLandingPage, getVersions]);

    // Use the formState hook to manage data fetching and state
    // Edit flow: Never show popup (per requirements)
    // Auto-populated fields (promptTemplate, taboolaPixel) are now marked as initial
    const { submit, submitStep } = useFormState({
        formDataFetcher,
        disableDataChangeDetection: true,
    });

    // Create a submit wrapper that handles publishing
    const handleSubmitWrapper = useCallback(
        () =>
            submit({
                saveFormData: publishHandler,
                successUrl: LANDING_PAGES_ROUTE,
            }),
        [submit, publishHandler]
    );

    const handleSubmitStepWrapper = useCallback(
        () =>
            submitStep({
                saveFormData: chatSubmitHandler,
            }),
        [submitStep, chatSubmitHandler]
    );

    return {
        submit: handleSubmitWrapper,
        submitStep: handleSubmitStepWrapper,
    };
};
